import Swal from "sweetalert2";
import "sweetalert2/dist/sweetalert2.min.css";

const escapeHtml = (value) =>
	String(value ?? "")
		.replace(/&/g, "&amp;")
		.replace(/</g, "&lt;")
		.replace(/>/g, "&gt;")
		.replace(/"/g, "&quot;")
		.replace(/'/g, "&#039;");

const normalizeMessage = (message) => {
	if (message instanceof Error) return message.message || "Something went wrong.";
	if (typeof message === "object" && message !== null) {
		try {
			return JSON.stringify(message, null, 2);
		} catch (e) {
			return String(message);
		}
	}
	return String(message ?? "");
};

const toSafeHtml = (message) => escapeHtml(normalizeMessage(message)).replace(/\n/g, "<br/>");

let alertQueue = Promise.resolve();

const enqueueSwal = (runner) => {
	alertQueue = alertQueue.catch(() => undefined).then(runner);
	return alertQueue;
};

export const sweetConfirm = (message, options = {}) => {
	const safeHtml = toSafeHtml(message);

	return enqueueSwal(async () => {
		const result = await Swal.fire({
			title: options.title || "Please Confirm",
			html: `<div class="cs-alert-message">${safeHtml}</div>`,
			icon: options.icon || "warning",
			iconColor: "#FF8C00",
			showCancelButton: true,
			confirmButtonText: options.confirmButtonText || "Yes",
			cancelButtonText: options.cancelButtonText || "Cancel",
			allowOutsideClick: false,
			allowEscapeKey: true,
			buttonsStyling: false,
			customClass: {
				popup: "cs-swal-popup",
				title: "cs-swal-title",
				confirmButton: "cs-swal-confirm-btn",
				cancelButton: "cs-swal-cancel-btn",
			},
		});

		return !!result.isConfirmed;
	});
};

const inferStatusFromMessage = (messageValue = "") => {
	const text = String(messageValue ?? "").toLowerCase().trim();
	if (!text) return "";

	if (/\b(success|successful|saved|updated|uploaded|created|completed|sent|published|logout successful|logged in)\b/.test(text)) {
		return "success";
	}

	if (/\b(fail|failed|error|invalid|not found|unable|cannot|can't|denied|forbidden|exception)\b/.test(text)) {
		return "fail";
	}

	if (/\b(warn|warning|required|empty|missing|unsaved|full|limit)\b/.test(text)) {
		return "warning";
	}

	return "";
};

const resolveStatusMeta = (statusValue, messageValue = "") => {
	const explicitStatus = String(statusValue ?? "").trim().toLowerCase();
	const resolvedStatus = explicitStatus || inferStatusFromMessage(messageValue);

	if (["success", "ok", "passed"].includes(resolvedStatus)) {
		return { title: "Success", icon: "success", iconColor: "#16A34A" };
	}

	if (["fail", "failed", "error", "invalid"].includes(resolvedStatus)) {
		return { title: "Failed", icon: "error", iconColor: "#DC2626" };
	}

	if (["warning", "warn"].includes(resolvedStatus)) {
		return { title: "Warning", icon: "warning", iconColor: "#F59E0B" };
	}

	return { title: "Notice", icon: "info", iconColor: "#FF8C00" };
};


const extractStatusPayload = (payload) => {
	if (payload instanceof Error) {
		const status =
			payload?.response?.data?.Status ??
			payload?.response?.data?.status ??
			"";
		const messageCandidate =
			payload?.response?.data?.Message ??
			payload?.response?.data?.message ??
			payload.message ??
			"Something went wrong.";

		return {
			status,
			message: normalizeMessage(messageCandidate),
		};
	}

	if (
		typeof payload === "string" ||
		typeof payload === "number" ||
		typeof payload === "boolean"
	) {
		const raw = String(payload);
		const trimmed = raw.trim();

		if (trimmed.startsWith("{") && trimmed.endsWith("}")) {
			try {
				return extractStatusPayload(JSON.parse(trimmed));
			} catch (e) {
				// fallback to plain string
			}
		}

		return { status: "", message: raw };
	}

	if (payload && typeof payload === "object") {
		const responseData =
			payload?.response?.data && typeof payload.response.data === "object"
				? payload.response.data
				: null;

		const status =
			payload?.Status ??
			payload?.status ??
			responseData?.Status ??
			responseData?.status ??
			"";

		const messageCandidate =
			payload?.Message ??
			payload?.message ??
			responseData?.Message ??
			responseData?.message ??
			(typeof payload?.response?.data === "string" ? payload.response.data : "");

		return {
			status,
			message: normalizeMessage(messageCandidate || payload),
		};
	}

	return {
		status: "",
		message: "Something went wrong.",
	};
};

export const sweetStatusAlert = (payload, options = {}) => {
	const { status, message } = extractStatusPayload(payload);
	const meta = resolveStatusMeta(status, message);
	const safeHtml = toSafeHtml(message);

	return enqueueSwal(() =>
		Swal.fire({
			title: options.title || meta.title,
			html: `<div class="cs-alert-message text-sm sm:text-base leading-6">${safeHtml}</div>`,
			icon: options.icon || meta.icon,
			iconColor: options.iconColor || meta.iconColor,
			confirmButtonText: options.confirmButtonText || "OK",
			allowOutsideClick: false,
			allowEscapeKey: true,
			buttonsStyling: false,
			customClass: {
				popup: "cs-swal-popup",
				title: "cs-swal-title",
				confirmButton: "cs-swal-confirm-btn",
			},
		}),
	);
};

// export const setupSweetAlertForNativeAlert = () => {
// 	if (typeof window === "undefined" || window.__sweetAlertPatched__) return;

// 	window.__nativeAlert__ = window.alert;

// 	window.alert = (message) => {
// 		// const safeHtml = escapeHtml(normalizeMessage(message)).replace(/\n/g, "<br/>");
// 		const safeHtml = toSafeHtml(message);

// 		alertQueue = alertQueue
// 			.catch(() => undefined)
// 			.then(() =>
// 				Swal.fire({
// 					title: "Notice",
// 					html: `<div class="cs-alert-message">${safeHtml}</div>`,
// 					icon: "info",
// 					iconColor: "#FF8C00",
// 					confirmButtonText: "OK",
// 					allowOutsideClick: false,
// 					allowEscapeKey: true,
// 					buttonsStyling: false,
// 					customClass: {
// 						popup: "cs-swal-popup",
// 						title: "cs-swal-title",
// 						confirmButton: "cs-swal-confirm-btn",
// 					},
// 				}),
// 			);

// 		return undefined;
// 	};

// 	window.__sweetAlertPatched__ = true;
// };


export const setupSweetAlertForNativeAlert = () => {
	if (typeof window === "undefined" || window.__sweetAlertPatched__) return;

	window.__nativeAlert__ = window.alert;

	window.alert = (message) => {
		void sweetStatusAlert(message, {
			confirmButtonText: "OK",
		});
		return undefined;
	};

	window.__sweetAlertPatched__ = true;
};